export type FontMetrics = {
  ascent: number;
  descent: number;
  lineGap: number;
  unitsPerEm: number;
  xWidthAvg: number;
};

type FallbackFont = {
  local: string;
  metrics: FontMetrics;
};

export type CatalogFont = {
  family: string;
  file: string;
  weights: string;
  fallback: FallbackFont;
  systemStack: string;
  metrics: FontMetrics;
};

// Metrics from @capsizecss/metrics 4.4.0.
const ARIAL: FallbackFont = {
  local: 'Arial',
  metrics: {
    ascent: 1854,
    descent: -434,
    lineGap: 67,
    unitsPerEm: 2048,
    xWidthAvg: 913,
  },
};

const TIMES_NEW_ROMAN: FallbackFont = {
  local: 'Times New Roman',
  metrics: {
    ascent: 1825,
    descent: -443,
    lineGap: 87,
    unitsPerEm: 2048,
    xWidthAvg: 832,
  },
};

const SANS_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const SERIF_STACK = 'Georgia, "Times New Roman", serif';

export const FONT_CATALOG: Record<string, CatalogFont> = {
  BricolageGrotesque: {
    family: 'Bricolage Grotesque',
    file: '/fonts/bricolage-grotesque/bricolage-grotesque-latin-standard-normal.woff2',
    weights: '200 800',
    fallback: ARIAL,
    systemStack: SANS_STACK,
    metrics: {
      ascent: 930,
      descent: -270,
      lineGap: 0,
      unitsPerEm: 1000,
      xWidthAvg: 470,
    },
  },
  InstrumentSans: {
    family: 'Instrument Sans',
    file: '/fonts/instrument-sans/instrument-sans-latin-wght-normal.woff2',
    weights: '400 700',
    fallback: ARIAL,
    systemStack: SANS_STACK,
    metrics: {
      ascent: 970,
      descent: -250,
      lineGap: 0,
      unitsPerEm: 1000,
      xWidthAvg: 458,
    },
  },
  Fredoka: {
    family: 'Fredoka',
    file: '/fonts/fredoka/fredoka-latin-wght-normal.woff2',
    weights: '300 700',
    fallback: ARIAL,
    systemStack: SANS_STACK,
    metrics: {
      ascent: 974,
      descent: -236,
      lineGap: 0,
      unitsPerEm: 1000,
      xWidthAvg: 454,
    },
  },
  Nunito: {
    family: 'Nunito',
    file: '/fonts/nunito/nunito-latin-wght-normal.woff2',
    weights: '200 1000',
    fallback: ARIAL,
    systemStack: SANS_STACK,
    metrics: {
      ascent: 1011,
      descent: -353,
      lineGap: 0,
      unitsPerEm: 1000,
      xWidthAvg: 452,
    },
  },
  CormorantGaramond: {
    family: 'Cormorant Garamond',
    file: '/fonts/cormorant-garamond/cormorant-garamond-latin-wght-normal.woff2',
    weights: '300 700',
    fallback: TIMES_NEW_ROMAN,
    systemStack: SERIF_STACK,
    metrics: {
      ascent: 924,
      descent: -287,
      lineGap: 0,
      unitsPerEm: 1000,
      xWidthAvg: 394,
    },
  },
  DmSans: {
    family: 'DM Sans',
    file: '/fonts/dm-sans/dm-sans-latin-wght-normal.woff2',
    weights: '100 1000',
    fallback: ARIAL,
    systemStack: SANS_STACK,
    metrics: {
      ascent: 992,
      descent: -310,
      lineGap: 0,
      unitsPerEm: 1000,
      xWidthAvg: 466,
    },
  },
  Anton: {
    family: 'Anton',
    file: '/fonts/anton/anton-latin-400-normal.woff2',
    weights: '400',
    fallback: ARIAL,
    systemStack: 'Impact, sans-serif',
    metrics: {
      ascent: 2409,
      descent: -674,
      lineGap: 0,
      unitsPerEm: 2048,
      xWidthAvg: 828,
    },
  },
  Inter: {
    family: 'Inter',
    file: '/fonts/inter/inter-latin-wght-normal.woff2',
    weights: '100 900',
    fallback: ARIAL,
    systemStack: SANS_STACK,
    metrics: {
      ascent: 1984,
      descent: -494,
      lineGap: 0,
      unitsPerEm: 2048,
      xWidthAvg: 978,
    },
  },
};

export function catalogFont(key: string): CatalogFont {
  const found = FONT_CATALOG[key];
  if (!found) {
    throw new Error(`Font [${key}] is not in the font catalog.`);
  }

  return found;
}

function fallbackFamily(font: CatalogFont): string {
  return `${font.family} Fallback`;
}

export function fontStack(font: CatalogFont): string {
  return `"${font.family}", "${fallbackFamily(font)}", ${font.systemStack}`;
}

function percent(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

export function fallbackOverrides(font: CatalogFont): {
  sizeAdjust: string;
  ascentOverride: string;
  descentOverride: string;
  lineGapOverride: string;
} {
  const { metrics } = font;
  const sizeAdjust =
    metrics.xWidthAvg /
    metrics.unitsPerEm /
    (font.fallback.metrics.xWidthAvg / font.fallback.metrics.unitsPerEm);

  return {
    sizeAdjust: percent(sizeAdjust),
    ascentOverride: percent(metrics.ascent / metrics.unitsPerEm / sizeAdjust),
    descentOverride: percent(
      Math.abs(metrics.descent) / metrics.unitsPerEm / sizeAdjust,
    ),
    lineGapOverride: percent(metrics.lineGap / metrics.unitsPerEm / sizeAdjust),
  };
}

export function fontFaces(font: CatalogFont): string {
  const overrides = fallbackOverrides(font);

  return [
    `@font-face{font-family:"${font.family}";font-style:normal;font-weight:${font.weights};font-display:swap;src:url(${font.file}) format("woff2");}`,
    `@font-face{font-family:"${fallbackFamily(font)}";src:local("${font.fallback.local}");size-adjust:${overrides.sizeAdjust};ascent-override:${overrides.ascentOverride};descent-override:${overrides.descentOverride};line-gap-override:${overrides.lineGapOverride};}`,
  ].join('');
}
